import React, { useEffect, useState } from 'react';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd/es/form';
import { Heading } from '../Heading';
import { Card } from '../Card';
import useOrderTypeStore, { OrderTypeEnum } from '@/stores/useOrderTypeStore';
import { getOrderTypeTextKey } from '@/utils';

interface OrderTypeCardProps {
  form: FormInstance;
  orderStatus?: string;
  onOrderTypeChange?: (value: OrderTypeEnum) => void;
}

const orderTypeOptions: { value: OrderTypeEnum; labelKey: string }[] = [
  { value: OrderTypeEnum.NORMAL, labelKey: 'normal' },
  { value: OrderTypeEnum.RETURN, labelKey: 'return_order' },
  { value: OrderTypeEnum.EXCHANGE, labelKey: 'exchange' },
];

/**
 * OrderTypeCard component for selecting the type of order (NORMAL Order, Return, Exchange)
 * @param form - Form instance for managing form state
 * @param orderStatus - Optional order status to determine if the card should be disabled
 */
const OrderTypeCard: React.FC<OrderTypeCardProps> = ({ form, orderStatus, onOrderTypeChange }) => {
  const [selectedOrderType, setSelectedOrderType] = useState<OrderTypeEnum>(OrderTypeEnum.NORMAL);
  const { t } = useTranslation();

  const { setOrderType } = useOrderTypeStore();

  const initialOrderType = form.getFieldValue('orderType');

  useEffect(() => {
    if (initialOrderType) {
      setSelectedOrderType(initialOrderType);
    }
  }, [initialOrderType]);

  const handleSelect = (value: OrderTypeEnum) => {
    if (isDisabled) return;
    setSelectedOrderType(value);
    form.setFieldValue('orderType', value);
    setOrderType(value);
    onOrderTypeChange?.(value);
  };

  const isDisabled = !!orderStatus && orderStatus !== 'new';

  return (
    <Card>
      <Heading
        title={t('order_type')}
        fontSize='text-xs'
        fontWeight='font-bold'
        color='text-sd-muted-foreground'
        extraClassName='uppercase tracking-wider mb-4'
      />
      <Form.Item name='orderType' initialValue={OrderTypeEnum.NORMAL} noStyle>
        <input type='hidden' />
      </Form.Item>
      <div className='flex gap-2'>
        {orderTypeOptions.map((option) => {
          const isSelected = selectedOrderType === option.value;
          return (
            <button
              key={option.value}
              type='button'
              onClick={() => handleSelect(option.value)}
              disabled={isDisabled}
              className={`flex-1 rounded-lg border border-solid px-3 py-2.5 text-xs font-semibold uppercase transition-colors cursor-pointer ${
                isSelected
                  ? 'border-sd-primary bg-sd-primary/20 text-sd-foreground'
                  : 'border-sd-input bg-sd-card text-sd-muted-foreground hover:border-sd-primary/50'
              } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {String(t(option.labelKey))}
            </button>
          );
        })}
      </div>
      {selectedOrderType !== OrderTypeEnum.NORMAL && (
        <div className='mt-3 rounded-lg bg-[hsl(0,80%,95%)] px-3 py-2.5 text-xs font-medium text-[hsl(0,65%,45%)] uppercase'>
          {String(t(getOrderTypeTextKey(selectedOrderType)))}
        </div>
      )}
    </Card>
  );
};

export default OrderTypeCard;
